import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabase/supabase';

const emptySettings = {
  online_order_fee_enabled: false,
  online_order_fee_cents: 199,
  online_order_fee_tax_code: '',
  direct_charge_enabled: false,
  liability_verified: false,
  negative_balance_verified: false,
  fee_transfer_verified: false,
  tax_treatment_verified: false,
  account_compatibility_verified: false,
  runtime_ready: false,
  runtime_version: null,
  runtime_verified_at: null,
  sandbox_direct_charge_enabled: false,
  sandbox_runtime_ready: false,
  sandbox_runtime_version: null,
  sandbox_runtime_verified_at: null
};

const checks = [
  ['liability_verified', 'Stripe liability reviewed'],
  ['negative_balance_verified', 'Negative-balance exposure reviewed'],
  ['fee_transfer_verified', 'Application-fee transfer behavior reviewed'],
  ['tax_treatment_verified', 'Fee and direct-charge tax treatment verified'],
  ['account_compatibility_verified', 'Existing connected-account compatibility verified']
];

const getFunctionErrorMessage = async (error) => {
  const response = error?.context;

  if (response && typeof response.clone === 'function') {
    try {
      const body = await response.clone().json();
      if (body?.error) return body.error;
      if (body?.message) return body.message;
    } catch {
      try {
        const text = await response.clone().text();
        if (text) return text;
      } catch {
        // Keep the Supabase error message as the fallback.
      }
    }

    if (response.status) {
      return `Runtime verification failed (HTTP ${response.status}).`;
    }
  }

  return error?.message || 'Runtime verification failed.';
};

const getReviewValues = (source) => Object.fromEntries(
  checks.map(([key]) => [key, Boolean(source[key])])
);

const isCurrentVerification = (ready, version, verifiedAt) => {
  const parsedAt = Date.parse(verifiedAt || '');

  return ready === true
    && version === 'localmarket-direct-charge-v2'
    && Number.isFinite(parsedAt)
    && parsedAt <= Date.now()
    && parsedAt > Date.now() - 24 * 60 * 60 * 1000;
};

const AdminDirectChargeRolloutPanel = () => {
  const [settings, setSettings] = useState(emptySettings);
  const [savedReviews, setSavedReviews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [runtimeMessage, setRuntimeMessage] = useState('');
  const [sandboxVerified, setSandboxVerified] = useState(false);
  const missingChecks = checks.filter(([key]) => !settings[key]);
  const missingSavedChecks = checks.filter(([key]) => !savedReviews?.[key]);
  const reviewsAreSaved = savedReviews !== null
    && checks.every(([key]) => Boolean(settings[key]) === savedReviews[key]);
  const directChargeBlocked = !settings.runtime_ready || missingChecks.length > 0;
  const verifiedAt = Date.parse(settings.runtime_verified_at || '');
  const runtimeVerificationCurrent = settings.runtime_ready === true
    && settings.runtime_version === 'localmarket-direct-charge-v2'
    && Number.isFinite(verifiedAt)
    && verifiedAt <= Date.now()
    && verifiedAt > Date.now() - 24 * 60 * 60 * 1000;
  const directChargeReleaseBlocked = directChargeBlocked || !runtimeVerificationCurrent;
  const sandboxVerifiedAt = Date.parse(settings.sandbox_runtime_verified_at || '');
  const sandboxRuntimeCurrent = settings.sandbox_runtime_ready === true
    && settings.sandbox_runtime_version === 'localmarket-direct-charge-v2'
    && Number.isFinite(sandboxVerifiedAt)
    && sandboxVerifiedAt <= Date.now()
    && sandboxVerifiedAt > Date.now() - 24 * 60 * 60 * 1000;
  const sandboxReleaseBlocked = !sandboxRuntimeCurrent
    || missingChecks.length > 0
    || !reviewsAreSaved
    || missingSavedChecks.length > 0;

  useEffect(() => {
    let active = true;

    supabase.rpc('admin_get_stripe_payment_rollout_1793069000000')
      .then(({ data, error: requestError }) => {
        if (!active) return;
        if (requestError) {
          setError(requestError.message);
        } else {
          const loadedSettings = { ...emptySettings, ...(data || {}) };
          setSettings(loadedSettings);
          setSavedReviews(getReviewValues(loadedSettings));
          setSandboxVerified(isCurrentVerification(
            loadedSettings.sandbox_runtime_ready,
            loadedSettings.sandbox_runtime_version,
            loadedSettings.sandbox_runtime_verified_at
          ));
        }
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const update = (key, value) => {
    setSettings((current) => ({ ...current, [key]: value }));
    setSandboxVerified(false);
    setRuntimeMessage('');
    setSaved(false);
  };

  const saveRolloutSettings = async ({ preserveDirectCharge = true } = {}) => {
    const { data, error: saveError } = await supabase.rpc(
      'admin_update_stripe_payment_rollout_1793069000000',
      {
        p_online_order_fee_enabled: settings.online_order_fee_enabled,
        p_online_order_fee_cents: Number(settings.online_order_fee_cents),
        p_online_order_fee_tax_code: settings.online_order_fee_tax_code || null,
        p_direct_charge_enabled: preserveDirectCharge
          && settings.direct_charge_enabled
          && !directChargeReleaseBlocked,
        p_liability_verified: settings.liability_verified,
        p_negative_balance_verified: settings.negative_balance_verified,
        p_fee_transfer_verified: settings.fee_transfer_verified,
        p_tax_treatment_verified: settings.tax_treatment_verified,
        p_account_compatibility_verified: settings.account_compatibility_verified
      }
    );

    if (saveError) throw saveError;

    setSettings((current) => ({
      ...current,
      direct_charge_enabled: data?.direct_charge_enabled === true,
      runtime_ready: data?.runtime_ready === true,
      updated_at: data?.updated_at || new Date().toISOString()
    }));
    setSavedReviews(getReviewValues(settings));
    setSaved(true);
  };

  const saveReviews = async () => {
    setSaving(true);
    setError('');
    setSaved(false);

    try {
      await saveRolloutSettings({ preserveDirectCharge: false });
    } catch (saveError) {
      setError(saveError.message || 'Unable to save review checks.');
    } finally {
      setSaving(false);
    }
  };

  const saveSandboxRollout = async () => {
    setSaving(true);
    setError('');
    setSaved(false);

    try {
      const { data, error: saveError } = await supabase.rpc(
        'admin_update_sandbox_direct_charge_1793080000000',
        { p_enabled: settings.sandbox_direct_charge_enabled }
      );

      if (saveError) throw saveError;

      setSettings((current) => ({
        ...current,
        sandbox_direct_charge_enabled:
          data?.sandbox_direct_charge_enabled === true,
        sandbox_runtime_ready: data?.sandbox_runtime_ready === true,
        sandbox_runtime_version: data?.sandbox_runtime_version || null,
        sandbox_runtime_verified_at:
          data?.sandbox_runtime_verified_at || null
      }));
      setSaved(true);
    } catch (saveError) {
      setError(saveError.message || 'Unable to save sandbox test settings.');
    } finally {
      setSaving(false);
    }
  };

  const verifyRuntime = async () => {
    setVerifying(true);
    setError('');
    setRuntimeMessage('');
    setSandboxVerified(false);

    try {
      const { data, error: verifyError } = await supabase.functions.invoke(
        'stripe-direct-charge-runtime',
        { body: { action: 'verify' } }
      );

      if (verifyError) {
        throw new Error(await getFunctionErrorMessage(verifyError));
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      if (data?.sandbox_verified === true) {
        setSandboxVerified(true);
        setSettings((current) => ({
          ...current,
          sandbox_runtime_ready: data.sandbox_runtime_ready === true,
          sandbox_runtime_version: data.runtime_version || null,
          sandbox_runtime_verified_at: data.runtime_verified_at || new Date().toISOString()
        }));
        setRuntimeMessage(
          'Sandbox checks passed. This verifies test-mode credentials and account readiness only; production readiness was not changed. Verify with live Stripe credentials to unlock direct-charge activation.'
        );
        return;
      }

      if (data?.runtime_ready !== true) {
        throw new Error('Runtime verification failed.');
      }

      setSettings((current) => ({
        ...current,
        runtime_ready: true,
        runtime_version: data.runtime_version,
        runtime_verified_at: data.runtime_verified_at
      }));
      setRuntimeMessage(
        `Verified ${data.environment || 'production'} runtime ${data.runtime_version} and flow ${data.flow_version}.`
      );
    } catch (verifyError) {
      setSandboxVerified(false);
      setError(verifyError.message || 'Runtime verification failed.');
    } finally {
      setVerifying(false);
    }
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSaved(false);

    try {
      await saveRolloutSettings();
    } catch (saveError) {
      setError(saveError.message || 'Unable to save payment rollout settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="rounded-2xl bg-white p-6 text-sm font-bold text-indigo-600">Loading payment rollout settings...</div>;
  }

  return (
    <form onSubmit={save} className="space-y-5 rounded-[2rem] border border-amber-200 bg-white p-6 shadow-sm">
      <div>
        <h3 className="text-lg font-black text-slate-900">Online-order fee and direct charges</h3>
        <p className="mt-1 text-sm leading-relaxed text-slate-600">
          The $1.99 CAD online-order fee is retained by the platform. Direct-charge activation remains blocked until runtime version checks and all five reviews pass.
        </p>
        {!settings.runtime_ready && sandboxVerified ? (
          <p className="mt-2 text-sm font-bold text-amber-700">
            Sandbox checks passed, but production runtime readiness requires a successful live-mode verification. Saving these settings will not enable direct charges.
          </p>
        ) : !settings.runtime_ready ? (
          <p className="mt-2 text-sm font-bold text-amber-700">
            The server runtime has not passed production verification. Saving these settings does not enable direct charges.
          </p>
        ) : null}
        {settings.runtime_ready && !runtimeVerificationCurrent && (
          <p className="mt-2 text-sm font-bold text-amber-700">
            Runtime verification is expired or does not match the required release version. Direct-charge activation is blocked.
          </p>
        )}
      </div>

      {error && <p className="rounded-xl bg-red-50 p-3 text-sm font-bold text-red-700">{error}</p>}
      {saved && <p className="rounded-xl bg-emerald-50 p-3 text-sm font-bold text-emerald-700">Settings saved. Direct-charge activation remains subject to the server runtime and all five verification checks.</p>}
      {runtimeMessage && <p className="rounded-xl bg-emerald-50 p-3 text-sm font-bold text-emerald-700">{runtimeMessage}</p>}

      <label className="flex items-start gap-3 rounded-xl bg-slate-50 p-4">
        <input
          type="checkbox"
          checked={settings.online_order_fee_enabled}
          onChange={(event) => update('online_order_fee_enabled', event.target.checked)}
        />
        <span>
          <strong className="block text-sm text-slate-900">Enable online-order fee</strong>
          <small className="text-xs text-slate-500">CAD only. Offline orders remain fee-free.</small>
          <small className="block text-xs text-slate-500">The platform retains this fee; applicable refunds are reflected in finance reporting.</small>
        </span>
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-xs font-bold text-slate-600">
          Online-order fee: $1.99 CAD (199 cents)
          <input
            type="number"
            min="199"
            max="199"
            step="1"
            value={settings.online_order_fee_cents}
            readOnly
            className="mt-2 w-full rounded-xl border border-slate-200 p-3 text-sm"
          />
        </label>
        <label className="text-xs font-bold text-slate-600">
          Verified Stripe Tax code
          <input
            type="text"
            value={settings.online_order_fee_tax_code || ''}
            onChange={(event) => update('online_order_fee_tax_code', event.target.value)}
            placeholder="txcd_..."
            className="mt-2 w-full rounded-xl border border-slate-200 p-3 text-sm"
          />
        </label>
      </div>

      <div className="space-y-2 rounded-xl border border-slate-100 p-4">
        <p className="text-xs font-black uppercase tracking-widest text-slate-500">Required before direct charges can be enabled</p>
        {checks.map(([key, label]) => (
          <label key={key} className="flex items-center gap-3 text-sm font-medium text-slate-700">
            <input
              type="checkbox"
              checked={Boolean(settings[key])}
              onChange={(event) => update(key, event.target.checked)}
            />
            {label}
          </label>
        ))}
        {!reviewsAreSaved && (
          <p className="pt-1 text-xs font-bold text-amber-700">
            Review changes are not saved yet. Save the five review checks before runtime verification.
          </p>
        )}
        {missingSavedChecks.length > 0 && (
          <p className="pt-1 text-xs font-bold text-amber-700">
            Saved reviews still missing: {missingSavedChecks.map(([, label]) => label).join('; ')}.
          </p>
        )}
        <button
          type="button"
          disabled={saving || reviewsAreSaved}
          onClick={saveReviews}
          className="mt-2 rounded-xl border border-indigo-200 px-4 py-2 text-xs font-black uppercase tracking-widest text-indigo-700 disabled:opacity-50"
        >
          {saving ? 'Saving reviews...' : 'Save five review checks'}
        </button>
      </div>

      <div className="rounded-xl border border-slate-100 p-4">
        <strong className="block text-sm text-slate-900">Server runtime</strong>
        <p className="mt-1 text-xs leading-relaxed text-slate-600">
          Runtime verification checks Stripe credentials, a payment-ready connected account, all five reviews, and the configured runtime and flow versions. Live accounts are required for production readiness; sandbox accounts can be used for testing without changing production readiness.
        </p>
        <p className="mt-1 text-xs leading-relaxed text-slate-600">
          Runtime version settings identify the release expected by the verifier; they do not independently prove that lifecycle tests ran or that deployed handlers match tested code.
        </p>
        {sandboxVerified && (
          <p className="mt-2 text-xs font-bold text-amber-700">
            Test mode verified. Production runtime remains unverified.
          </p>
        )}
        {settings.sandbox_runtime_ready && (
          <p className="mt-2 text-xs font-bold text-amber-700">
            Sandbox verified {settings.sandbox_runtime_verified_at ? new Date(settings.sandbox_runtime_verified_at).toLocaleString() : ''} ({settings.sandbox_runtime_version || 'version unavailable'}). Test-mode readiness only.
          </p>
        )}
        {settings.runtime_ready && (
          <p className="mt-2 text-xs font-bold text-emerald-700">
            Verified {settings.runtime_verified_at ? new Date(settings.runtime_verified_at).toLocaleString() : ''} ({settings.runtime_version || 'version unavailable'})
          </p>
        )}
        <button
          type="button"
          disabled={verifying || saving || !reviewsAreSaved || missingSavedChecks.length > 0}
          onClick={verifyRuntime}
          className="mt-3 rounded-xl border border-indigo-200 px-4 py-2 text-xs font-black uppercase tracking-widest text-indigo-700 disabled:opacity-50"
        >
          {verifying ? 'Verifying runtime...' : 'Verify deployed server runtime'}
        </button>
      </div>

      {directChargeReleaseBlocked && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <strong className="block">Direct-charge activation blocked</strong>
          {!runtimeVerificationCurrent && sandboxVerified && (
            <p className="mt-1">Sandbox verification passed, but a current live-mode runtime verification is required before production direct charges can be enabled.</p>
          )}
          {!runtimeVerificationCurrent && !sandboxVerified && (
            <p className="mt-1">The deployed server runtime is not verified with a current runtime version.</p>
          )}
          {missingChecks.length > 0 && (
            <p className="mt-1">Outstanding reviews: {missingChecks.map(([, label]) => label).join('; ')}.</p>
          )}
        </div>
      )}

      <label className="flex items-start gap-3 rounded-xl bg-slate-50 p-4">
        <input
          type="checkbox"
          checked={settings.direct_charge_enabled}
          disabled={directChargeReleaseBlocked && !settings.direct_charge_enabled}
          onChange={(event) => update('direct_charge_enabled', event.target.checked)}
        />
        <span>
          <strong className="block text-sm text-slate-900">Enable direct-charge rollout</strong>
          <small className="text-xs text-slate-500">
            Sandbox verification does not unlock production activation. A current live-mode runtime verification and all five saved reviews are required.
          </small>
        </span>
      </label>

      <button
        disabled={saving}
        className="rounded-xl bg-indigo-600 px-5 py-3 text-xs font-black uppercase tracking-widest text-white disabled:opacity-50"
      >
        {saving ? 'Saving...' : 'Save payment rollout settings'}
      </button>

      <div className="space-y-3 rounded-xl border border-indigo-100 bg-indigo-50/50 p-4">
        <div>
          <strong className="block text-sm text-slate-900">Sandbox direct-charge testing</strong>
          <p className="mt-1 text-xs leading-relaxed text-slate-600">
            A separate test-mode flag allows direct-charge checkout with Stripe test credentials only. It does not change production readiness or the production rollout.
          </p>
        </div>
        <label className="flex items-start gap-3 rounded-xl bg-white p-4">
          <input
            type="checkbox"
            checked={settings.sandbox_direct_charge_enabled}
            disabled={sandboxReleaseBlocked && !settings.sandbox_direct_charge_enabled}
            onChange={(event) => update('sandbox_direct_charge_enabled', event.target.checked)}
          />
          <span>
            <strong className="block text-sm text-slate-900">Enable sandbox direct-charge tests</strong>
            <small className="text-xs text-slate-500">
              Requires current test-mode verification and all five saved reviews. Never applies to live Stripe keys.
            </small>
          </span>
        </label>
        {sandboxReleaseBlocked && (
          <p className="text-xs font-bold text-amber-700">
            Verify the test-mode runtime and save all five reviews before enabling sandbox direct-charge tests.
          </p>
        )}
        <button
          type="button"
          disabled={saving || (settings.sandbox_direct_charge_enabled && sandboxReleaseBlocked)}
          onClick={saveSandboxRollout}
          className="rounded-xl border border-indigo-200 px-4 py-2 text-xs font-black uppercase tracking-widest text-indigo-700 disabled:opacity-50"
        >
          {saving ? 'Saving sandbox setting...' : 'Save sandbox setting'}
        </button>
      </div>
    </form>
  );
};

export default AdminDirectChargeRolloutPanel;
